import React, { useEffect } from 'react';
import { clsx } from 'clsx';

import ProBadge from '@strava/icons/BadgesMulticolorProSmall';
import SubscriberBadge from '@strava/icons/BadgesMulticolorSummitSmall';
import VerifiedBadge from '@strava/icons/BadgesMulticolorVerifiedSmall';
import AvatarFallback from '@strava/icons/NavigationProfileHighlightedMedium';
import { useSafeState } from '@strava/react-hooks/use-safe-state';

import { RebrandVerifiedBadge } from './RebrandVerifiedBadge';
import { SubscriberShield } from './SubscriberShield';

import styles from './Avatar.module.scss';

interface ImgProps {
  src: string;
  alt: string;
  title: string;
  fallback?: React.ReactNode;
}

const Img = ({ src, alt, title, fallback = null }: ImgProps) => {
  const [children, setChildren] = useSafeState<React.ReactNode | null>(
    <img src={src} alt={alt} title={title} />
  );

  useEffect(() => {
    let image: HTMLImageElement | null = new Image();
    image.onload = () => {
      setChildren(<img src={src} alt={alt} title={title} />);
    };
    image.onerror = () => {
      setChildren(fallback);
    };
    image.src = src;
    return () => {
      image = null;
    };
  }, [src, alt, title, fallback, setChildren]);

  return <>{children}</>;
};

function getBadge(badge: string) {
  switch (badge) {
    case 'ambassador':
      return <ProBadge />;
    case 'employee':
    case 'subscriber':
    case 'premium':
      return <SubscriberBadge />;
    case 'pro':
      return <ProBadge />;
    case 'verified':
      return <VerifiedBadge />;
    default:
      return null;
  }
}

export const avatarTypes = ['athlete', 'club'] as const;
export type AvatarType = (typeof avatarTypes)[number];

export const avatarSizes = [
  'xsmall',
  'small',
  'default',
  'large',
  'xlarge'
] as const;
export type AvatarSize = (typeof avatarSizes)[number];

export const avatarBadges = [
  '', // in some cases active has member_type as empty string for default users,
  'ambassador',
  'employee',
  'subscriber',
  'premium', // legacy name referring to 'subscriber', used in active
  'pro',
  'verified'
] as const;
export type AvatarBadge = (typeof avatarBadges)[number];

interface BaseAvatarProps {
  src: string;
  type: AvatarType;
  name?: string;
  alt?: string;
  title?: string;
  size?: AvatarSize;
  badge?: AvatarBadge;
  className?: string;
  showRebrand?: boolean;
}

type AnchorProps = React.ComponentPropsWithoutRef<'a'> & {
  href?: string;
};
type DivProps = React.ComponentPropsWithoutRef<'div'> & {
  href?: never;
};

// Guard to check if href exists in props
const hasHref = (props: AnchorProps | DivProps): props is AnchorProps =>
  'href' in props;

export type AvatarProps = BaseAvatarProps & (AnchorProps | DivProps);

/**
 * Prop Deprecation Warning: We are slowly deprecating the `name` prop in favor of the `title` prop
 * so that the React props match up to the element's attribute.
 * */
export const Avatar = ({
  name = '',
  alt = '',
  title,
  type,
  src,
  size,
  badge,
  className,
  showRebrand = false,
  ...otherProps
}: AvatarProps) => {
  const classNames = clsx([
    styles.avatar,
    styles[type],
    className,
    size && styles[size]
  ]);

  // TODO: uncomment when existing Avatar usage is refactored. Otherwise, there will be a lot of warning in the console.
  // if (name || process.env.NODE_ENV !== 'production') {
  //   console.warn(
  //     '[Prop Deprecation Warning] Deprecating the `name` prop in favor of the `title`.'
  //   );
  // }

  const shouldShowShieldIcon = [
    'ambassador',
    'employee',
    'subscriber',
    'premium',
    'pro',
    'verified'
  ].includes(badge as string);

  // Hardcoded this for testing / future deployment
  showRebrand = true;

  const children = (
    <>
      {badge && !showRebrand && (
        <div className={styles.badge}>{getBadge(badge)}</div>
      )}
      {badge && badge === 'verified' && showRebrand && (
        <div className={styles.rebrandBadge}>{<RebrandVerifiedBadge />}</div>
      )}
      {shouldShowShieldIcon && type === 'athlete' && showRebrand ? (
        <SubscriberShield
          src={src}
          alt={alt}
          size={size}
          title={title || name}
          data-testid="subscriber-shield"
        />
      ) : (
        <div className={styles.imgWrapper}>
          <Img
            src={src}
            alt={alt}
            title={title || name}
            fallback={type === 'athlete' ? <AvatarFallback /> : null}
          />
        </div>
      )}
    </>
  );

  if (hasHref(otherProps)) {
    return (
      <a className={classNames} data-testid="avatar-wrapper" {...otherProps}>
        {children}
      </a>
    );
  }

  return (
    <div
      className={classNames}
      data-testid="avatar-wrapper"
      {...(otherProps as DivProps)}
    >
      {children}
    </div>
  );
};
